/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { MutableRef } from 'preact/hooks';
import type { ColumnWidths } from '#UNSAFE_TableView';
import type { BorderEdges, SampledWidths, TableColumn, ViewportInfo } from '../Table';
import { TABLE_HEADER_SELECTOR } from './TableDomUtils';

/**
 * Helper method to extract the individual column widths of the Table
 * depending on its layout. With a 'fixed' layout, an initial 'availableWidth'
 * is required, while with a 'contents' layout, -1 can be provided as the
 * 'availableWidth' initially. This will trigger the layout logic to ensure
 * a non-contstrained Table width is supported, and is needed to match
 * current Redwood behaviors with a 'contents' layout.
 */
export function applyLayoutColumnWidths<K, D, C extends string>(
  scrollerElement: HTMLElement,
  viewportInfo: ViewportInfo,
  innerTableElement: HTMLTableElement,
  widthOffset: number,
  tableId: string,
  isFixed: boolean,
  sampledColumnNetWidthsRef: MutableRef<SampledWidths<C> | undefined>,
  availableRealWidth: number,
  columnsArray: TableColumn<K, D, C>[],
  columnBorderEdges: BorderEdges,
  borderOffset: number,
  staticColumnWidths?: ColumnWidths<C>
) {
  let newNetColumnWidths;
  let totalRealWidth = 0;
  let realWidthsArray = [];
  const tableCols = innerTableElement.querySelectorAll(
    `[data-oj-table-col='${tableId}']`
  ) as NodeListOf<HTMLTableColElement>;

  let totalBorderWidth = 0;
  for (const borderEdge of columnBorderEdges) {
    totalBorderWidth += borderEdge.width;
  }
  const availableNetWidth = availableRealWidth !== -1 ? availableRealWidth - totalBorderWidth : -1;

  if (isFixed) {
    newNetColumnWidths = _getFixedLayoutColumnNetWidths(
      availableNetWidth,
      columnsArray,
      staticColumnWidths
    );
    for (let i = 0; i < columnsArray.length; i++) {
      const width = newNetColumnWidths[i] + columnBorderEdges[i].width;
      realWidthsArray.push(width);
      totalRealWidth += width;
    }
    _updateTableSizing(innerTableElement, tableCols, realWidthsArray, totalRealWidth);
    return _createColumnWidths(
      columnsArray,
      newNetColumnWidths,
      realWidthsArray,
      columnBorderEdges,
      borderOffset,
      totalBorderWidth
    );
  }

  const headerElements = innerTableElement.querySelectorAll(
    `[${TABLE_HEADER_SELECTOR}='${tableId}']`
  ) as NodeListOf<HTMLElement>;

  newNetColumnWidths = _getContentsLayoutColumnNetWidths(
    innerTableElement,
    headerElements,
    sampledColumnNetWidthsRef,
    availableNetWidth,
    columnsArray,
    columnBorderEdges,
    staticColumnWidths
  );
  for (let i = 0; i < columnsArray.length; i++) {
    const realWidth = newNetColumnWidths[i] + columnBorderEdges[i].width;
    realWidthsArray.push(realWidth);
    totalRealWidth += realWidth;
  }
  _updateTableSizing(innerTableElement, tableCols, realWidthsArray, totalRealWidth);

  if (availableRealWidth === -1) {
    // for content sizing, check to see if all space is actually utilized
    // this supports no width constraint scenarios
    if (!viewportInfo.isExternalScroller) {
      availableRealWidth = scrollerElement.getBoundingClientRect().width - widthOffset;
    } else {
      availableRealWidth = scrollerElement.clientWidth - widthOffset;
    }
    if (Math.abs(totalRealWidth - availableRealWidth) > 0.005) {
      newNetColumnWidths = _getContentsLayoutColumnNetWidths(
        innerTableElement,
        headerElements,
        sampledColumnNetWidthsRef,
        availableRealWidth - totalBorderWidth,
        columnsArray,
        columnBorderEdges,
        staticColumnWidths
      );
      totalRealWidth = 0;
      realWidthsArray = [];
      for (let i = 0; i < columnsArray.length; i++) {
        const width = newNetColumnWidths[i] + columnBorderEdges[i].width;
        realWidthsArray.push(width);
        totalRealWidth += width;
      }
      _updateTableSizing(innerTableElement, tableCols, realWidthsArray, totalRealWidth);
    }
  }
  return _createColumnWidths(
    columnsArray,
    newNetColumnWidths,
    realWidthsArray,
    columnBorderEdges,
    borderOffset,
    totalBorderWidth
  );
}

/**
 * Helper method to extract the individual column widths of the Table
 * for a 'fixed' layout table that already has its root width applied.
 */
function _getFixedLayoutColumnNetWidths<K, D, C extends string>(
  availableNetWidth: number,
  columnsArray: TableColumn<K, D, C>[],
  staticColumnWidths?: ColumnWidths<C>
) {
  const DEFAULT_COLUMN_WEIGHT = 1;
  const DEFAULT_COLUMN_MIN_WIDTH = 100;
  const DEFAULT_COLUMN_MAX_WIDTH = undefined;

  let requiresActiveSizing = false;
  let totalWorkingWeight = 0;
  let forcedTotalNetWidth = 0;
  let pendingTotalNetWidth = 0;
  const columnMaxWidthsArray = [];
  const columnWeightsArray = [];
  const forcedColumnNetWidthsArray = [];
  const newColumnNetWidthsArray = [];
  const appliedColumnNetWidthsArray = [];
  for (let i = 0; i < columnsArray.length; i++) {
    const column = columnsArray[i];
    let columnMinWidth = column.value.minWidth;
    if (columnMinWidth == null || columnMinWidth <= 0) {
      columnMinWidth = DEFAULT_COLUMN_MIN_WIDTH;
    }
    let columnMaxWidth = column.value.maxWidth;
    if (columnMaxWidth == null || columnMaxWidth < columnMinWidth) {
      columnMaxWidth = DEFAULT_COLUMN_MAX_WIDTH;
    }
    columnMaxWidthsArray.push(columnMaxWidth);
    let columnWeight = column.value.weight;
    if (columnWeight == null || columnWeight < 1) {
      columnWeight = DEFAULT_COLUMN_WEIGHT;
    }
    columnWeightsArray.push(columnWeight);
    const columnWidth = staticColumnWidths != null ? staticColumnWidths[column.key] : undefined;
    if (columnWidth != null) {
      forcedTotalNetWidth += columnWidth;
      pendingTotalNetWidth += columnWidth;
      forcedColumnNetWidthsArray.push(columnWidth);
      newColumnNetWidthsArray.push(columnWidth);
      appliedColumnNetWidthsArray.push(columnWidth);
    } else {
      requiresActiveSizing = true;
      pendingTotalNetWidth += columnMinWidth;
      totalWorkingWeight += columnWeight;
      forcedColumnNetWidthsArray.push(undefined);
      newColumnNetWidthsArray.push(columnMinWidth);
      appliedColumnNetWidthsArray.push(columnMinWidth);
    }
  }

  // enforce min and max rules if active sizing is required and available space remains
  if (requiresActiveSizing && pendingTotalNetWidth < availableNetWidth) {
    _enforceFixedMinMaxRules(
      columnsArray,
      availableNetWidth,
      totalWorkingWeight,
      forcedTotalNetWidth,
      forcedColumnNetWidthsArray,
      columnWeightsArray,
      columnMaxWidthsArray,
      newColumnNetWidthsArray,
      appliedColumnNetWidthsArray
    );
  }
  return newColumnNetWidthsArray;
}

/**
 * Helper function to enforce all of the layout sizing rules on the Table's columns.
 */
function _enforceFixedMinMaxRules<K, D, C>(
  columnsArray: TableColumn<K, D, C>[],
  availableWidth: number,
  totalWorkingWeight: number,
  forcedTotalWidth: number,
  forcedColumnWidthsArray: (number | undefined)[],
  columnWeightsArray: number[],
  columnMaxWidthsArray: (number | undefined)[],
  newColumnWidthsArray: number[],
  appliedColumnWidthsArray: number[]
) {
  // try and honor the weights of each column combined with the min widths, and then check that against
  // any max widths once completed. if the final result doesn't fill the space AND at least 1 max width
  // rule was enforced, repeat the process until no max rules are enforced OR the space is filled.
  let isMaxRuleApplied = true;
  while (isMaxRuleApplied) {
    isMaxRuleApplied = false;
    _enforceWeightedMinRules(
      columnsArray,
      availableWidth,
      totalWorkingWeight,
      forcedTotalWidth,
      forcedColumnWidthsArray,
      columnWeightsArray,
      newColumnWidthsArray,
      appliedColumnWidthsArray
    );
    let pendingTotalWidth = 0;
    for (let i = 0; i < columnsArray.length; i++) {
      if (forcedColumnWidthsArray[i] == null) {
        const maxWidth = columnMaxWidthsArray[i];
        if (maxWidth != null && newColumnWidthsArray[i] > maxWidth) {
          isMaxRuleApplied = true;
          forcedColumnWidthsArray[i] = maxWidth;
          forcedTotalWidth += maxWidth;
          totalWorkingWeight -= columnWeightsArray[i];
          newColumnWidthsArray[i] = maxWidth;
          appliedColumnWidthsArray[i] = maxWidth;
        }
      }
      pendingTotalWidth += newColumnWidthsArray[i];
    }
    isMaxRuleApplied = isMaxRuleApplied && pendingTotalWidth < availableWidth;
  }
}

/**
 * Helper method to extract the individual column widths of the Table
 * for a 'contents' layout table that already has its root width applied.
 */
function _getContentsLayoutColumnNetWidths<K, D, C extends string>(
  innerTableElement: HTMLTableElement,
  headerElements: NodeListOf<HTMLElement>,
  sampledColumnNetWidthsRef: MutableRef<SampledWidths<C> | undefined>,
  availableNetWidth: number,
  columnsArray: TableColumn<K, D, C>[],
  columnBorderEdges: { hasStart: boolean; hasEnd: boolean; width: number }[],
  staticColumnWidths?: ColumnWidths<C>
) {
  const DEFAULT_COLUMN_MIN_WIDTH = undefined;
  const DEFAULT_COLUMN_MAX_WIDTH = undefined;

  let requiresActiveSizing = false;
  const columnMinWidthsArray = [] as (number | undefined)[];
  const columnMaxWidthsArray = [];
  const forcedColumnNetWidthsArray = [];
  const newColumnNetWidthsArray = [];
  for (let i = 0; i < columnsArray.length; i++) {
    const column = columnsArray[i];
    let columnMinWidth = column.value.minWidth;
    if (columnMinWidth == null || columnMinWidth <= 0) {
      columnMinWidth = DEFAULT_COLUMN_MIN_WIDTH;
    }
    columnMinWidthsArray.push(columnMinWidth);
    let columnMaxWidth = column.value.maxWidth;
    if (columnMaxWidth == null || columnMaxWidth < (columnMinWidth != null ? columnMinWidth : 0)) {
      columnMaxWidth = DEFAULT_COLUMN_MAX_WIDTH;
    }
    columnMaxWidthsArray.push(columnMaxWidth);
    const columnWidth = staticColumnWidths != null ? staticColumnWidths[column.key] : undefined;
    if (columnWidth != null) {
      forcedColumnNetWidthsArray.push(columnWidth);
      newColumnNetWidthsArray[i] = columnWidth;
    } else {
      requiresActiveSizing = true;
      forcedColumnNetWidthsArray.push(undefined);
    }
  }

  // short-circuit normal contents sizing if each column specifies a 'width' value
  if (requiresActiveSizing) {
    // if active sizing is required, update min width values to reflect actual widths
    // performance note - this should be the only logic that requires DOM measurements
    let forcedTotalNetWidth = 0;
    let pendingTotalNetWidth = 0;
    let totalPreferredNetWidth = 0;
    let totalWorkingWeight = 0;
    let totalRemainingWeight = 0;
    const sampledNetWidths = sampledColumnNetWidthsRef.current;
    const sampledMinNetWidthsArray = [];
    const sampledWeightNetWidthsArray = [] as number[];
    const appliedColumnNetWidthsArray = [];
    if (sampledNetWidths != null) {
      for (let i = 0; i < columnsArray.length; i++) {
        sampledMinNetWidthsArray.push(sampledNetWidths.minWidths[columnsArray[i].key]);
        sampledWeightNetWidthsArray.push(sampledNetWidths.weightWidths[columnsArray[i].key]);
      }
    } else {
      sampledColumnNetWidthsRef.current = {
        minWidths: {} as ColumnWidths<C>,
        weightWidths: {} as ColumnWidths<C>
      };
      // when sampledColumnNetWidths are not populated, table has 'max-content' width applied,
      // which allows for measurements to represent the column weights
      for (let i = 0; i < headerElements.length; i++) {
        const sampledWeightNetWidth =
          (headerElements[i] as HTMLElement).getBoundingClientRect().width -
          columnBorderEdges[i].width;
        sampledWeightNetWidthsArray.push(sampledWeightNetWidth);
        sampledColumnNetWidthsRef.current.weightWidths[columnsArray[i].key] = sampledWeightNetWidth;
      }
      // specify 'min-content' width, allowing measurements to represent minimum auto-resizable column widths
      innerTableElement.style.width = 'min-content';
      for (let i = 0; i < headerElements.length; i++) {
        const sampledMinNetWidth =
          (headerElements[i] as HTMLElement).getBoundingClientRect().width -
          columnBorderEdges[i].width;
        sampledMinNetWidthsArray.push(sampledMinNetWidth);
        sampledColumnNetWidthsRef.current.minWidths[columnsArray[i].key] = sampledMinNetWidth;
      }
    }

    for (let i = 0; i < sampledMinNetWidthsArray.length; i++) {
      const forcedWidth = forcedColumnNetWidthsArray[i];
      if (forcedWidth == null) {
        let columnNetWidth;
        const sampledMinNetWidth = sampledMinNetWidthsArray[i];
        const minWidth = columnMinWidthsArray[i];
        const maxWidth = columnMaxWidthsArray[i];
        if (minWidth != null && minWidth >= sampledMinNetWidth) {
          columnNetWidth = minWidth;
        } else if (maxWidth != null && sampledMinNetWidth >= maxWidth) {
          columnNetWidth = maxWidth;
        } else {
          columnNetWidth = sampledMinNetWidth;
        }
        pendingTotalNetWidth += columnNetWidth;
        // replace sampled weight with min width if needed
        const columnWeight =
          minWidth != null && minWidth >= sampledWeightNetWidthsArray[i]
            ? minWidth
            : sampledWeightNetWidthsArray[i];
        sampledWeightNetWidthsArray[i] = columnWeight;
        totalPreferredNetWidth +=
          maxWidth != null && maxWidth <= columnWeight ? maxWidth : columnWeight;
        totalWorkingWeight += columnWeight;
        totalRemainingWeight += columnWeight - columnNetWidth;
        // newColumnWidthsArray already has content, so update in place rather than 'push'
        newColumnNetWidthsArray[i] = columnNetWidth;
        appliedColumnNetWidthsArray.push(columnNetWidth);
      } else {
        forcedTotalNetWidth += forcedWidth;
        pendingTotalNetWidth += forcedWidth;
        totalPreferredNetWidth += forcedWidth;
        appliedColumnNetWidthsArray.push(forcedWidth);
      }
    }
    // special case for initial rendering where we don't know if overall width is constrained
    if (availableNetWidth === -1) {
      availableNetWidth = totalPreferredNetWidth;
    }
    if (pendingTotalNetWidth < availableNetWidth) {
      _enforceContentsMinMaxRules(
        columnsArray,
        availableNetWidth,
        totalPreferredNetWidth,
        totalRemainingWeight,
        totalWorkingWeight,
        pendingTotalNetWidth,
        forcedTotalNetWidth,
        forcedColumnNetWidthsArray,
        sampledWeightNetWidthsArray,
        columnMaxWidthsArray,
        newColumnNetWidthsArray,
        appliedColumnNetWidthsArray
      );
    }
  }
  return newColumnNetWidthsArray;
}

/**
 * Helper function to enforce all of the layout sizing rules on the Table's columns.
 */
function _enforceContentsMinMaxRules<K, D, C>(
  columnsArray: TableColumn<K, D, C>[],
  availableWidth: number,
  totalPreferredWidth: number,
  totalRemainingWeight: number,
  totalWorkingWeight: number,
  appliedPendingTotalWidth: number,
  forcedTotalWidth: number,
  forcedColumnWidthsArray: (number | undefined)[],
  columnWeightsArray: number[],
  columnMaxWidthsArray: (number | undefined)[],
  newColumnWidthsArray: number[],
  appliedColumnWidthsArray: number[]
) {
  // try and honor the weights of each column combined with the min widths, and then check that against
  // any max widths once completed. if the final result doesn't fill the space AND at least 1 max width
  // rule was enforced, repeat the process until no max rules are enforced OR the space is filled.
  let isMaxRuleApplied = true;
  while (isMaxRuleApplied) {
    isMaxRuleApplied = false;
    if (totalPreferredWidth > availableWidth) {
      _enforceConstrainedWeightRules(
        columnsArray,
        availableWidth - appliedPendingTotalWidth,
        totalRemainingWeight,
        columnWeightsArray,
        forcedColumnWidthsArray,
        newColumnWidthsArray,
        appliedColumnWidthsArray
      );
    } else {
      _enforceWeightedMinRules(
        columnsArray,
        availableWidth,
        totalWorkingWeight,
        forcedTotalWidth,
        forcedColumnWidthsArray,
        columnWeightsArray,
        newColumnWidthsArray,
        appliedColumnWidthsArray
      );
    }

    let pendingTotalWidth = 0;
    for (let i = 0; i < columnsArray.length; i++) {
      if (forcedColumnWidthsArray[i] == null) {
        const maxWidth = columnMaxWidthsArray[i];
        if (maxWidth != null && newColumnWidthsArray[i] > maxWidth) {
          isMaxRuleApplied = true;
          forcedColumnWidthsArray[i] = maxWidth;
          forcedTotalWidth += maxWidth;
          appliedPendingTotalWidth += maxWidth - appliedColumnWidthsArray[i];
          totalWorkingWeight -= columnWeightsArray[i];
          newColumnWidthsArray[i] = maxWidth;
          appliedColumnWidthsArray[i] = maxWidth;
        }
      }
      pendingTotalWidth += newColumnWidthsArray[i];
    }
    isMaxRuleApplied = isMaxRuleApplied && pendingTotalWidth < availableWidth;
  }
}

/**
 * Helper function to enforce constrained weights / min width rules on the Table's columns.
 */
function _enforceConstrainedWeightRules<K, D, C>(
  columnsArray: TableColumn<K, D, C>[],
  initialExtraWidth: number,
  totalRemainingWeight: number,
  columnWeightsArray: number[],
  forcedColumnWidthsArray: (number | undefined)[],
  newColumnWidthsArray: number[],
  appliedColumnWidthsArray: number[]
) {
  if (initialExtraWidth > 0) {
    let availableWidth = initialExtraWidth;
    let currentWeightTotal = totalRemainingWeight;
    for (let i = 0; i < columnsArray.length; i++) {
      if (currentWeightTotal > 0 && forcedColumnWidthsArray[i] == null) {
        const currentWeight = columnWeightsArray[i] - appliedColumnWidthsArray[i];
        const weightWidth = (currentWeight / currentWeightTotal) * availableWidth;
        newColumnWidthsArray[i] = weightWidth + appliedColumnWidthsArray[i];
        availableWidth -= weightWidth;
        currentWeightTotal -= currentWeight;
      }
    }
  }
}

/**
 * Helper function to enforce the weights / min width rules on the Table's columns.
 */
function _enforceWeightedMinRules<K, D, C>(
  columnsArray: TableColumn<K, D, C>[],
  availableWidth: number,
  totalWeight: number,
  forcedTotalWidth: number,
  forcedColumnWidthsArray: (number | undefined)[],
  columnWeightsArray: number[],
  newColumnWidthsArray: number[],
  appliedColumnWidthsArray: number[]
) {
  const pendingForcedColumnWidths = [] as (number | undefined)[];
  let isMinRuleApplied = true;
  while (isMinRuleApplied) {
    isMinRuleApplied = false;
    let currentWeightTotal = totalWeight;
    let widthDiff = availableWidth - forcedTotalWidth;
    if (widthDiff > 0) {
      for (let i = 0; i < columnsArray.length; i++) {
        if (
          currentWeightTotal > 0 &&
          forcedColumnWidthsArray[i] == null &&
          pendingForcedColumnWidths[i] == null
        ) {
          const currentWeight = columnWeightsArray[i];
          const newWidth = (currentWeight / currentWeightTotal) * widthDiff;

          // if calculated width breaks min width requirement, force min width as needed
          const minWidth = appliedColumnWidthsArray[i];
          if (newWidth < minWidth) {
            // the preferred weighted size conflicts with the min size, so set pending to min size
            pendingForcedColumnWidths[i] = minWidth;
            forcedTotalWidth += minWidth;
            newColumnWidthsArray[i] = minWidth;
            isMinRuleApplied = true;
            totalWeight -= currentWeight;
            break;
          }
          newColumnWidthsArray[i] = newWidth;
          widthDiff -= newWidth;
          currentWeightTotal -= currentWeight;
        }
      }
    }
  }
}

/**
 * Helper function to create a ColumnWidths object from a TableColumn array and a widths array.
 */
function _createColumnWidths<K, D, C extends string>(
  columnsArray: TableColumn<K, D, C>[],
  netWidthsArray: number[],
  realWidthsArray: number[],
  borderEdges: BorderEdges,
  borderOffset: number,
  totalBorderOffset: number
) {
  const logicalWidthsArray: number[] = [];
  const columnWidths = {} as ColumnWidths<C>;
  for (let i = 0; i < columnsArray.length; i++) {
    columnWidths[columnsArray[i].key] = netWidthsArray[i];
    logicalWidthsArray.push(netWidthsArray[i] + borderEdges[i].offset);
  }
  return {
    netColumnWidths: columnWidths,
    fullColumnWidths: logicalWidthsArray,
    realColumnWidths: realWidthsArray,
    borderEdges,
    borderOffset,
    totalBorderOffset
  };
}

/**
 * Helper method to apply the given sizing object to the currently rendered Table
 * and column elements.
 */
function _updateTableSizing(
  innerTableElement: HTMLTableElement,
  tableCols: NodeListOf<HTMLTableColElement>,
  realWidthsArray: number[],
  totalRealWidth: number
) {
  for (let i = 0; i < tableCols.length; i++) {
    const tableCol = tableCols[i];
    tableCol.style.width = `${realWidthsArray[i]}px`;
  }
  innerTableElement.style.width = `${totalRealWidth}px`;
  innerTableElement.style.tableLayout = 'fixed';
}

/**
 * Helper function to compare two ColumnWidths instances for equality.
 */
export const columnWidthsComparator = <C extends string>(
  columnWidths1: ColumnWidths<C>,
  columnWidths2: ColumnWidths<C>
) => {
  const keys1 = Object.keys(columnWidths1);
  const keys2 = Object.keys(columnWidths2);
  if (keys1.length === keys2.length) {
    for (const key of keys1) {
      const columnKey = key as C;
      if (columnWidths1[columnKey] !== columnWidths2[columnKey]) {
        return false;
      }
    }
    return true;
  }
  return false;
};
